import { useState, useEffect } from "react";
import { Zap, Shield, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { createPageUrl } from "@/utils";

// Target date: January 1, 2026
const TARGET_DATE = new Date("2026-04-01T00:00:00").getTime();

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function useCountdown(targetDate: number): TimeLeft {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculate = () => {
      const now = Date.now();
      const diff = Math.max(0, targetDate - now);

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

interface CountdownUnitProps {
  value: number;
  label: string;
}

function CountdownUnit({ value, label }: CountdownUnitProps) {
  return (
    <div className="flex flex-col items-center animate-pop">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-800/80 border border-slate-700 rounded-xl flex items-center justify-center shadow-lg backdrop-blur-sm transition-transform hover:scale-105 duration-300">
        <span className="text-2xl sm:text-3xl font-bold text-white font-mono">
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span className="text-[10px] sm:text-xs text-slate-400 mt-2 uppercase tracking-wider font-medium">
        {label}
      </span>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const countdown = useCountdown(TARGET_DATE);

  // Redirect wenn eingeloggt
  useEffect(() => {
    if (!loading && user) {
      navigate(createPageUrl("Dashboard"));
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden relative font-sans selection:bg-indigo-500/30">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] animate-pulse-soft" />
        <div
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[100px] animate-pulse-soft"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-600/5 rounded-full blur-[150px] animate-pulse-soft"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      <main className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 py-16">
        <div className="w-full max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="animate-float inline-block">
            <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/30 rounded-full px-4 py-1.5 mb-8 backdrop-blur-md hover:bg-indigo-500/20 transition-colors duration-300 cursor-default">
              <Zap className="w-4 h-4 text-indigo-400" />
              <span className="text-xs sm:text-sm font-medium text-indigo-300">
                Coming Soon · 2026
              </span>
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold leading-tight mb-6 tracking-tight animate-slide-up-fade">
            <span className="text-white">Stop Burning Leads.</span>
            <br />
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent bg-300% animate-gradient">
              Start Booking Meetings.
            </span>
          </h1>

          <p
            className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto mb-12 leading-relaxed animate-slide-up-fade"
            style={{ animationDelay: "100ms" }}
          >
            The world's first AI-powered Cold Call Dojo. Train against realistic AI buyer personas —
            CFO, Gatekeeper, Founder — before you ever pick up the phone.
          </p>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-3 mb-16">
            {[
              { icon: Target, label: "AI Buyer Personas" },
              { icon: Shield, label: "Zero Risk Practice" },
              { icon: Zap, label: "Instant Feedback" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-slate-800/50 border border-slate-700/50 rounded-full px-4 py-2 hover:bg-slate-800 transition-all hover:scale-105 duration-300 cursor-default animate-pop"
                style={{ animationDelay: `${200 + i * 100}ms` }}
              >
                <item.icon className="w-4 h-4 text-indigo-400" />
                <span className="text-sm text-slate-300">{item.label}</span>
              </div>
            ))}
          </div>

          {/* Countdown Section */}
          <div className="mb-8 animate-slide-up-fade" style={{ animationDelay: "500ms" }}>
            <p className="text-xs sm:text-sm text-slate-400 uppercase tracking-widest mb-6 font-medium opacity-80">
              Open Beta Starts In
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              <CountdownUnit value={countdown.days} label="Days" />
              <span className="text-2xl text-slate-600 font-light mt-[-20px] hidden sm:block animate-pulse">
                :
              </span>
              <CountdownUnit value={countdown.hours} label="Hours" />
              <span className="text-2xl text-slate-600 font-light mt-[-20px] hidden sm:block animate-pulse">
                :
              </span>
              <CountdownUnit value={countdown.minutes} label="Minutes" />
              <span className="text-2xl text-slate-600 font-light mt-[-20px] hidden sm:block animate-pulse">
                :
              </span>
              <CountdownUnit value={countdown.seconds} label="Seconds" />
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-slate-800/50 py-6 px-6 backdrop-blur-sm bg-slate-950/50">
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <span>© 2025 Callevo.io</span>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-slate-300 transition-colors">
              Privacy
            </a>
            <a href="#" className="hover:text-slate-300 transition-colors">
              Imprint
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
